<template lang="html">
  <div class="mt-6">
    <!-- Output selection display -->
    <div v-show="!show.mlt_video_queued && !show.mlt_video_converting">
      <!-- Select conversion settings.-->
      <b-list-group>
        <b-list-group-item
          v-for="setting in output_settings"
          :key="'list' + setting.id"
          class="flex-column align-items-start list-button"
          :variant="returnVariant(setting)"
          :disabled="setting.premium && $store.getters.is_trial"
          @click="output_selected = setting.id"
        >
          <div class="d-flex w-100 justify-content-between">
            <h5 class="mb-1">
              {{ setting.display_name }}
              <v-icon
                v-if="setting.id == output_selected"
                name="tv"
                class="output-checked ml-1"
              />
            </h5>

            <small
              v-if="
                showAspectRatio == setting.aspect && !showMatchingAspectOnly
              "
            >
              Aspect ratio <v-icon name="check" />
            </small>
          </div>
          <small>
            {{ setting.width }}w x {{ setting.height }}h
            <span class="text-muted">
              -
              <span>{{ returnAspectLabel(setting) }}</span>
            </span>
          </small>
          <small v-if="setting.premium && $store.getters.is_trial">
            - <span>upgrade option</span>
          </small>
          <small v-if="setting.beta_enabled" class="float-right">
            Beta export option <v-icon name="flask" />
          </small>
          <small v-if="setting.enabled == false" class="float-right">
            Admin only <v-icon name="exclamation-triangle" />
          </small>
        </b-list-group-item>
      </b-list-group>

      <div v-if="showMatchingAspectOnly" class="mt-3 small">
        <p>
          Displaying only options matching your show's aspect ratio.
          <b-link size="sm" @click="showMatchingAspectOnly = false">
            Show all options.
          </b-link>
        </p>
      </div>

      <div v-if="!showMatchingAspectOnly" class="mt-3 small">
        <p>
          Displaying all export options.
          <b-link size="sm" @click="showMatchingAspectOnly = true">
            Show only options matching your show's aspect ratio.
          </b-link>
        </p>
        <p class="mt-1 mb-1">
          <v-icon name="check" /> Denotes an output option matching your show's
          current aspect ratio.
        </p>
      </div>

      <div class="mt-3 small text-muted">
        <p v-if="hasBetaSetting" class="mb-1">
          <v-icon name="flask" /> Denotes an option available via our
          <router-link :to="{ name: 'account' }">best testing</router-link>
          program.
        </p>
        <p class="mt-0">
          Change your aspect ratio via
          <router-link
            :to="{ name: 'show-mode', params: { mode: 'settings' } }"
          >
            your show's settings
          </router-link>
          page.
        </p>
      </div>

      <div v-if="show.data.displaySettings.autoShowCaptions" class="mt-3 small">
        <p>
          <v-icon name="info-circle" /> Captions will be exported in your video.
          Uncheck the <em>Show captions by default</em> option in your
          timeline's Project inspector to disable.
        </p>
      </div>

      <!-- User is a trial -->
      <div v-if="$store.getters.is_trial" class="mt-4">
        <p v-if="show.mlt_video_completed">
          <b-btn
            :disabled="show.mlt_video_queued || show.mlt_video_converting"
            size="lg"
            @click="queueForExportSeconds(60)"
          >
            Export trial again
          </b-btn>
          <span class="small ml-2">
            <mark>Trial accounts are limited to 60 second exports.</mark>
          </span>
        </p>
        <p v-if="!show.mlt_video_completed">
          <b-btn
            :disabled="show.mlt_video_queued || show.mlt_video_converting"
            @click="queueForExportSeconds(60)"
          >
            Start trial export
          </b-btn>
          <span class="small ml-2">
            <mark>Trial accounts are limited to 60 second exports</mark>
          </span>
        </p>
      </div>

      <!-- User is subscribed (not on a trial) -->
      <div v-if="!$store.getters.is_trial" class="mt-4">
        <p v-if="show.mlt_video_completed">
          <b-btn
            :disabled="show.mlt_video_queued || show.mlt_video_converting"
            size="lg"
            @click="queueForExport"
          >
            Export again
          </b-btn>
          <span class="small ml-2">
            Selected size: {{ output_selected_display_name }}
          </span>
        </p>
        <p v-if="!show.mlt_video_completed">
          <b-btn
            :disabled="show.mlt_video_queued || show.mlt_video_converting"
            @click="queueForExport"
          >
            Start export
          </b-btn>
          <span class="small ml-2">
            Output size selected: {{ output_selected_display_name }}
          </span>
        </p>
      </div>

      <div class="mt-4">
        <p>
          <b-btn
            v-if="show.mlt_video_completed"
            :disabled="show.mlt_video_queued || show.mlt_video_converting"
            size="sm"
            variant="outline-secondary"
            @click="queueForExportSeconds(30)"
          >
            Export 30 second sample
          </b-btn>
          <b-btn
            v-if="!show.mlt_video_completed"
            :disabled="show.mlt_video_queued || show.mlt_video_converting"
            size="sm"
            variant="outline-secondary"
            @click="queueForExportSeconds(30)"
          >
            Export 30 second sample
          </b-btn>
        </p>
        <p class="text-muted small">
          <v-icon name="stopwatch" /> Save time by exporting a small sample of
          your show for evaluation or client approval.
        </p>
      </div>
    </div>

    <div v-if="$store.getters.is_trial" class="mt-5">
      <p>
        <span class="mark mr-2">
          HD-enabled plans start at $5!
        </span>
        <b-btn
          :to="{ name: 'upgrade_view', params: { plan_name: 'basic' } }"
          size="sm"
          variant="outline-secondary"
        >
          View upgrade options
        </b-btn>
      </p>
      <p class="small mt-1">
        Upgrade for higher resolutions.
        <!--
        <span class="mark">Exported trial shows are watermarked.</span>
        -->
      </p>
    </div>

    <!-- Show is queued -->
    <div
      v-if="show.mlt_video_queued && !show.mlt_video_converting"
      class="alert alert-info mt-5"
    >
      <!-- Show is queued.-->
      <h3>Your show is now queued <v-icon name="circle-notch" spin /></h3>
      <p class="mt-3">Shows are processed in the order they are received.</p>
      <p>Average queue time is less than 2 minutes.</p>
    </div>

    <!-- Conversion in progress -->
    <div v-if="show.mlt_video_converting" class="alert alert-secondary">
      <h3 class="mb-3">Export in progress</h3>
      <div
        v-for="step in show.mlt_video_progress"
        :key="'list_' + step.id"
        class="ml-2"
      >
        <p>
          {{ step.order }}. {{ getDescription(step) }}
          <v-icon
            v-show="step.complete"
            name="check-circle"
            class="success ml-1"
          />
          <v-icon
            v-show="isInProgress(step)"
            name="circle-notch"
            spin
            class="inprogress ml-1"
          />
        </p>
        <b-progress v-if="step.id == 'render'" class="mt-2" :max="100">
          <b-progress-bar
            :value="progress"
            show-progress
            :label="`${progress}%`"
            :animated="true"
            variant="success"
          />
        </b-progress>
        <p v-if="step.id == 'render' && progress > 0" class="mt-2 small">
          Estimated time remaining:
          <strong>{{ estimatedTimeRemaining }}</strong>
        </p>
      </div>
    </div>

    <div v-if="debug && estimatedTimeRemaining > 0" class="small mt-5 col-7">
      <p>
        estimate render time:
        <span class="float-right">
          {{ estimatedProcessingTime | seconds_in_words }}
        </span>
      </p>
      <p>
        elasped render time:
        <span class="float-right">
          {{ elaspedProcessingTime | seconds_in_words }}
        </span>
      </p>
      <p>
        estimated render remaining:
        <span class="float-right">
          {{
            (estimatedProcessingTime - elaspedProcessingTime) | seconds_in_words
          }}
        </span>
      </p>
    </div>

    <!-- Conversion complete -->
    <div
      v-show="show.mlt_video_completed && !show.mlt_video_queued"
      class="mt-5"
    >
      <!-- Conversion complete. -->
      <h2>Export complete</h2>
      <p>Download your video file below.</p>
      <DownloadButtonVideo
        :key="show.mlt_video_uploaded_filename"
        :item_key="show.key + '/m/' + show.mlt_video_uploaded_filename"
      />
      <p class="small mt-2">
        Your file was completed
        {{ (show.mlt_video_completed_time * 1000) | age_in_words }} using the
        <strong>{{ returnDisplayName(show.mlt_video_preset) }}</strong>
        <span v-if="show.mlt_video_is_sample" class="mark">
          (short sample!)
        </span>
        option.
      </p>
    </div>
  </div>
</template>

<script>
import sortBy from "lodash/sortBy";
import filter from "lodash/filter";
import isFinite from "lodash/isFinite";
import map from "lodash/map";
import reverse from "lodash/reverse";
import first from "lodash/first";
import find from "lodash/find";
import uniq from "lodash/uniq";
import DownloadButtonVideo from "@/components/show/DownloadButtonVideo.vue";
import { formatDistanceToNow, addSeconds } from "date-fns";

export default {
  name: "ShowExport",
  components: { DownloadButtonVideo },
  filters: {
    age_in_words(value) {
      if (!isFinite(value)) {
        return "";
      }
      return formatDistanceToNow(new Date(value)) + " ago";
    },
    seconds_in_words(value) {
      if (!isFinite(value)) {
        return "";
      }

      var d = new Date();
      d = addSeconds(d, value);
      return formatDistanceToNow(d, { includeSeconds: true });
    },
  },
  data() {
    return {
      output_selected: null,
      showMatchingAspectOnly: true,
      stepDescriptions: {
        parse: "Parsing show files",
        download_assets: "Collecting images and audio",
        prep: "Creating video manifest",
        render_start: "Initializing renderer",
        render: "Rendering video",
        uploading: "Saving to server",
      },
    };
  },
  computed: {
    show() {
      return this.$store.state.show.currentShow;
    },
    hasBetaSetting() {
      return filter(this.output_settings, { beta_enabled: true }).length > 0;
    },
    output_selected_display_name() {
      var setting = find(this.output_settings, {
        id: this.output_selected,
      });
      if (setting) {
        return setting.display_name;
      } else {
        return this.output_selected;
      }
    },
    output_settings() {
      if (!this.$store.state.video_output_options) {
        return [];
      }
      var all_settings = Object.values(
        this.$store.state.video_output_options.mlt_settings || {}
      );
      var settings = all_settings;
      if (this.$store.state.user_doc.video_all_outputs_enabled != true) {
        // Limit to just the "enabled" settings.
        settings = filter(settings, { enabled: true });
      }
      // Add the beta options if the user is a beta user.
      if (this.$store.state.user_doc.beta_tester == true) {
        // Limit to just the "enabled" settings.
        console.log("Include beta items.");
        var beta_settings = filter(all_settings, { beta_enabled: true });
        settings.push(...beta_settings);
      }

      // Filter if matching aspect or not.
      if (this.showMatchingAspectOnly) {
        settings = filter(settings, { aspect: this.showAspectRatio });
      }

      settings = uniq(reverse(sortBy(settings, ["aspect", "height"])));

      return settings;
    },
    output_options() {
      return map(this.output_settings, (i) => {
        return {
          text: `${i.display_name} (${i.width}x${i.height})`,
          value: i.id,
          disabled: i.premium && this.$store.getters.is_trial,
        };
      });
    },
    progress() {
      if (this.lastStep) {
        if (this.lastStep.id === "render") {
          return this.lastStep.progress_percentage;
        }
      }
      return 0;
    },
    estimatedTimeRemaining() {
      var d = new Date();
      if (
        !isFinite(this.estimatedProcessingTime - this.elaspedProcessingTime)
      ) {
        return 0;
      }
      d = addSeconds(
        d,
        this.estimatedProcessingTime - this.elaspedProcessingTime
      );
      return formatDistanceToNow(d, { includeSeconds: true });
    },
    elaspedProcessingTime() {
      if (this.lastStep) {
        if (this.lastStep.id === "render") {
          var render_start_timestamp = find(this.show.mlt_video_progress, {
            id: "render_start",
          }).timestamp;

          return this.lastStep.timestamp - render_start_timestamp;
        }
      }
      return 0;
    },
    estimatedProcessingTime() {
      if (this.lastStep) {
        if (this.lastStep.id === "render") {
          var render_start_timestamp = find(this.show.mlt_video_progress, {
            id: "render_start",
          }).timestamp;

          return (
            ((this.lastStep.timestamp - render_start_timestamp) /
              this.lastStep.progress_percentage) *
            100
          );
        }
      }
      return 0;
    },
    lastStep() {
      return first(
        filter(this.show.mlt_video_progress, (s) => {
          return s.complete != true;
        })
      );
    },
    selected() {
      if (!this.$store.state.video_output_options) {
        return {};
      }
      return find(this.$store.state.video_output_options.mlt_settings, {
        id: this.output_selected,
      });
    },
    debug() {
      // Debug activated by "?debug"
      return process.env.NODE_ENV === "development";
    },
    showAspectRatio() {
      var aspectRatio = (this.show.data.displaySettings || {}).aspectRatio;
      if (!aspectRatio) {
        aspectRatio = 1.5;
      }
      return aspectRatio;
    },
  },
  watch: {
    is_trial(value) {
      if (value) {
        this.output_selected = "default_dv";
      }
    },
  },
  mounted() {
    this.$store.dispatch("load_video_output_options");
    // Set the initial default.
    if (this.$store.getters.is_trial) {
      this.output_selected = "504p";
      if (this.showAspectRatio == 1.5) {
        this.output_selected = "default_dv";
      }
    } else {
      this.output_selected = "hd2";
      if (this.showAspectRatio == 1.33333) {
        this.output_selected = "1080presentation";
      }
      if (this.showAspectRatio == 1.5) {
        this.output_selected = "dv1280";
      }
      if (this.showAspectRatio == 1) {
        this.output_selected = "square";
      }
      if (this.showAspectRatio == 0.5625) {
        this.output_selected = "1080vertical";
      }
      if (this.showAspectRatio == 0.8) {
        this.output_selected = "vertical-4-5";
      }
    }
  },
  methods: {
    returnAspectLabel(setting) {
      if (setting.aspect == 1.5) {
        return "3:2";
      }
      if (setting.aspect == 1) {
        return "1:1";
      }
      if (setting.aspect == 1.33333) {
        return "4:3";
      }
      if (setting.aspect == 0.5625) {
        return "9:16";
      }
      if (setting.aspect == 0.8) {
        return "4:5";
      }
      return "16:9";
    },
    returnDisplayName(preset) {
      var setting = find(this.output_settings, {
        preset: preset,
      });
      if (setting) {
        return setting.display_name;
      } else {
        return preset;
      }
    },
    returnVariant(setting) {
      if (this.output_selected == setting.id) {
        return "success";
      }
      return "secondary-outline";
    },
    queueForExportSeconds(sampleSeconds) {
      // Export a small sample of the show.
      this.queueForExport(sampleSeconds);
    },

    queueForExport(sampleSeconds) {
      var obj = {
        sampleSeconds: sampleSeconds,
        presetName: this.selected.preset,
      };
      this.$store.dispatch("show/queueForExport", obj);
    },
    getDescription(step) {
      return this.stepDescriptions[step.id];
    },
    isInProgress(step) {
      var lastStep = this.lastStep;
      if (lastStep) {
        return lastStep.id == step.id;
      } else {
        return false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.mt-6 {
  margin-top: 3.9em;
}
.list-button {
  cursor: pointer;
}
.output-checked {
  opacity: 0.2;
}
.size_left {
  position: absolute;
  left: 100px;
  top: 0px;
  width: auto;
  min-width: 100px;
}

.beta_left {
  position: absolute;
  left: 200px;
  width: auto;
  min-width: 80px;
  top: 0px;
}
</style>
